// Wareneingang – Belegart 'goods_receipt'. Der Standard-Vertragssatz (Entwurf, festschreiben, stornieren, drucken …)
// kommt aus der Belegfabrik; dazu der Abschluss am Telefon in einem Schritt und die Freigabe über der Grenze.
//
// Für das Modul Einkauf: Die Belegart nennt `derivesFrom: ['purchase_order']` und schreibt den Fortschritt
// `received`. Sobald es eine Belegart 'purchase_order' gibt (stock: { kind: 'incoming' }), legt
// `inventory.receipt.derive` mit { sources: [{ documentId, lines? }] } den Wareneingang aus der Bestellung an.
import { defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { APPROVAL_OUTCOMES, zDay, zNote, zReason, zUnitQty, zUnitQtyOrZero } from './common'

export const RECEIPT_TYPE = 'goods_receipt'
export const RECEIPT_SOURCE_TYPES = ['purchase_order'] as const

/** Zustände vor dem Festschreiben */
export const APPROVAL_PENDING = 'pending_approval'
export const APPROVAL_RETURNED = 'returned'

const PERMISSIONS = {
  read: 'inventory.receipt.read',
  write: 'inventory.receipt.create',
  post: 'inventory.receipt.create',
  reverse: 'inventory.receipt.approve',
} as const

export const receipt = defineDocumentContracts({
  module: 'inventory',
  entity: 'receipt',
  documentType: RECEIPT_TYPE,
  title: 'Wareneingang',
  titlePlural: 'Wareneingänge',
  permissions: PERMISSIONS,
  derivesFrom: RECEIPT_SOURCE_TYPES,
  keywords: ['Wareneingang', 'Lieferung', 'Anlieferung', 'einbuchen', 'Lieferschein', 'Zugang'],
})

export const receiptGet = receipt.get
export const receiptList = receipt.list
export const receiptSaveDraft = receipt.saveDraft
/** Festschreiben: bucht den Zugang – oder stellt den Beleg zur Freigabe, wenn sein Warenwert über der Grenze liegt */
export const receiptPost = receipt.post
export const receiptReverse = receipt.reverse
export const receiptTransition = receipt.transition
export const receiptPrint = receipt.print
export const receiptDiscard = receipt.discard
export const receiptDerive = receipt.derive

/** Abweichung einer Zeile gegenüber Lieferschein bzw. Bestellung – steht in `customFields` der Belegzeile. */
export const zReceiptDifference = z.strictObject({
  /** Fehlmenge im Gebinde der Zeile (Tausendstel): geliefert laut Lieferschein, aber nicht angekommen */
  shortQty: zUnitQtyOrZero.optional(),
  /** beschädigte Menge im Gebinde der Zeile (Tausendstel) – in der Zeilenmenge ENTHALTEN */
  damagedQty: zUnitQtyOrZero.optional(),
})

export const receiptReceive = defineContract({
  name: 'inventory.receipt.receive',
  title: 'Wareneingang erfassen und buchen',
  description: 'Erfasst eine Lieferung in einem Schritt (Scan am Telefon) und bucht sie – über der Grenze geht sie zur Freigabe.',
  llm: {
    description:
      'Erfasst einen Wareneingang OHNE Bestellung in einem Schritt und bucht ihn: Standort, optional Lieferant und ' +
      'Lieferschein-Nummer, je Zeile Artikel, Gebinde und Menge im Gebinde in Tausendstel (1 Karton = 1000), dazu ' +
      'Pflichtangaben, wo der Artikel sie verlangt, sowie Abweichungen (`shortQty` Fehlmenge, `damagedQty` beschädigt, ' +
      'beide im Gebinde). Den Einstandspreis schlägt das System aus den Einkaufspreisen vor. Liegt der Warenwert über ' +
      'der Grenze der Rolle, wird nichts gebucht: Der Beleg wartet auf Freigabe (`outcome: pending`). Für einen ' +
      'Entwurf, der später ergänzt wird, inventory.receipt.saveDraft; für Ware aus einer Umlagerung ' +
      'inventory.transfer.receive.',
    keywords: ['Wareneingang', 'Lieferung annehmen', 'einbuchen', 'Zugang', 'Lieferschein', 'Bruch', 'Fehlmenge'],
  },
  input: z.strictObject({
    locationId: zId,
    partnerId: zId.nullish(),
    /** Lieferschein-Nummer des Lieferanten */
    externalRef: z.string().trim().max(120).nullish(),
    documentDate: zDay.optional(),
    notes: z.string().trim().max(4000).nullish(),
    lines: z
      .array(
        z.strictObject({
          itemId: zId,
          unitId: zId.nullish(),
          qty: zUnitQty,
          lotNo: z.string().trim().max(80).nullish(),
          bestBefore: zDay.nullish(),
          /** Seriennummern der gelieferten Stücke – eine je Stück, nur bei Artikeln mit Seriennummernpflicht */
          serialNos: z.array(z.string().trim().min(1).max(80)).max(500).nullish(),
          shortQty: zUnitQtyOrZero.optional(),
          damagedQty: zUnitQtyOrZero.optional(),
          note: zNote,
        }),
      )
      .min(1, { error: 'Bitte erfassen Sie mindestens eine Position.' })
      .max(500),
  }),
  output: z.object({ outcome: z.enum(APPROVAL_OUTCOMES), document: zDocumentView, message: z.string() }),
  permission: PERMISSIONS.write,
  risk: 'financial',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
  limits: { maxItems: 500, itemCount: (input) => input.lines.length },
})

export const receiptApprove = defineContract({
  name: 'inventory.receipt.approve',
  title: 'Wareneingang freigeben',
  description: 'Gibt einen Wareneingang frei, der über der Grenze der erfassenden Person liegt, und bucht ihn.',
  llm: {
    description:
      'Gibt einen Wareneingang im Zustand „Zur Freigabe“ frei und bucht ihn (Bestand, Einstandswert, Nummer). ' +
      'Nur für Belege, die wegen der Wertgrenze warten. Zum Zurückgeben an die erfassende Person inventory.receipt.reject.',
    keywords: ['Wareneingang freigeben', 'Freigabe', 'genehmigen'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentView,
  permission: 'inventory.receipt.approve',
  risk: 'financial',
  scope: 'record',
  effects: { reversible: true },
})

export const receiptReject = defineContract({
  name: 'inventory.receipt.reject',
  title: 'Wareneingang zurückgeben',
  description: 'Gibt einen Wareneingang, der auf Freigabe wartet, mit Grund an die erfassende Person zurück.',
  llm: {
    description:
      'Lehnt die Freigabe eines Wareneingangs ab und gibt ihn mit Grund zurück: Er wird wieder bearbeitbar, gebucht ' +
      'wird nichts. Nur im Zustand „Zur Freigabe“.',
    keywords: ['Wareneingang ablehnen', 'zurückgeben', 'Freigabe ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDocumentView,
  permission: 'inventory.receipt.approve',
  risk: 'write',
  scope: 'record',
})
